import { Capacitor } from '@capacitor/core'
import { buildAppHeaders } from '@/data/api/api-headers'
import { secureTokenStore } from '@/services/auth/secure-token-store'

const PRIMARY_ENDPOINT_KEY = 'primaryApiEndpoint'
const FALLBACK_ENDPOINT_KEY = 'fallbackApiEndpoint'

// Baked-in production API bases. On native the WebView origin is an internal
// scheme (https://localhost) that can only serve the packaged web assets, so
// same-origin can never reach the API and would return index.html — these
// constants are the last-resort defaults there. They also guard web builds whose
// mode does not load .env.production (e.g. `vite build --mode native`).
// 生产数据只有一个权威入口。旧版本曾把 Worker 地址持久化到 localStorage，
// 升级后它仍会抢在当前服务器之前被使用，造成同一账号读取到旧空库（统计为 0、
// 复习无卡片）。2.8.0 起始终优先并仅使用当前站点/官方 API；持久化键仅保留给
// 旧登录迁移代码写入，不再参与生产请求路由，彻底避免数据源分叉。
const PRIMARY_API_BASE_URL = 'https://shangancard.me/api/v1'

function normalizeEndpoint(value: string) {
  return value.replace(/\/+$/, '')
}

export function getApiEndpoints() {
  const envPrimary = String(import.meta.env.VITE_API_BASE_URL || '')
  const isNative = Capacitor.isNativePlatform()
  // Native can never talk to same-origin; Web 始终使用当前页面同源 API，避免
  // Cookie、账号和数据库被路由到历史部署。
  const originPrimary = isNative ? PRIMARY_API_BASE_URL : `${location.origin}/api/v1`
  return [
    normalizeEndpoint(originPrimary),
    normalizeEndpoint(envPrimary),
  ].filter((value, index, values) => value && values.indexOf(value) === index)
}

export function saveApiEndpoints(primary: string, fallback = '') {
  localStorage.setItem(PRIMARY_ENDPOINT_KEY, normalizeEndpoint(primary))
  if (fallback.trim()) localStorage.setItem(FALLBACK_ENDPOINT_KEY, normalizeEndpoint(fallback))
  else localStorage.removeItem(FALLBACK_ENDPOINT_KEY)
}

export async function remoteApiRequest(path: string, init: RequestInit = {}) {
  const token = await secureTokenStore.getToken()
  const appHeaders = await buildAppHeaders()
  const endpoints = getApiEndpoints()
  let lastError: Error | null = null
  for (const endpoint of endpoints) {
    try {
      const headers = new Headers(init.headers)
      if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
      if (token) headers.set('Authorization', `Bearer ${token}`)
      for (const [name, value] of Object.entries(appHeaders)) {
        if (!headers.has(name)) headers.set(name, value)
      }
      const response = await fetch(`${endpoint}/${path.replace(/^\/+/, '')}`, {
        ...init,
        headers,
        credentials: Capacitor.isNativePlatform() ? 'omit' : 'include',
        signal: init.signal || AbortSignal.timeout(60000),
      })
      if (response.status >= 500) {
        lastError = new Error(`服务端暂时不可用（HTTP ${response.status}）`)
        continue
      }
      return response
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('网络请求失败')
    }
  }
  throw lastError || new Error('尚未配置可用的同步服务地址')
}
